import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import {
	useConnectFurnitureToEref,
	useDeleteFurnitureComment,
	useDeleteFurnitureCustomization,
	useDisconnectFurnitureFromEref,
	useGetComponentOptions,
	useLinkFurnitureCustomModelNumber,
	useSelectArbitraryModelNumber,
	useUpdateFurniture,
	useUpdateFurnitureCategory,
	useUpdateFurnitureComment,
	useUpdateFurnitureConfig,
	useUpdateFurnitureCountertopAttachments,
	useUpdateFurnitureCountertopSeries,
	useUpdateFurnitureCustomization,
	useUpdateFurnitureFixedPrice,
	useUpdateFurnitureStatus,
} from "hooks/api/quotation/quotations.hook"
import { useGetModelNumber, useGetModelNumberImageLink } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useGetSeries } from "hooks/api/series/series.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { reorderFurnitureInCache, spliceFurnitureIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import { FurnitureHeader } from "./layout/FurnitureHeader/FurnitureHeader"
import { FurnitureImage } from "./layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "./layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { DeliveryDateSection } from "./layout/DeliveryDateSection/DeliveryDateSection"
import { CountertopSeriesSection } from "./layout/CountertopSeriesSection/CountertopSeriesSection"
import { CategoriesSection } from "./layout/CategoriesSection/CategoriesSection"
import { DimensionsSection } from "./layout/DimensionsSection/DimensionsSection"
import { ModelNumberSection } from "./layout/ModelNumberSection/ModelNumberSection"
import { PriceSection } from "./layout/PriceSection/PriceSection"
import { NameSection } from "./layout/NameSection/NameSection"
import { AttachmentsSection } from "./layout/AttachmentsSection/AttachmentsSection"
import { NotesSection } from "./layout/NotesSection/NotesSection"
import { CustomizationSection } from "components/ui/customWebviewer/shared/CustomizationSection/CustomizationSection"
import { FurnitureFooter } from "./layout/FurnitureFooter/FurnitureFooter"
import { ConnectedCalloutsTab } from "./layout/ConnectedCalloutsTab/ConnectedCalloutsTab"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import {
	asCustomFurniture,
	asOtherFurniture,
	isCustomFurniture,
	isOtherFurniture,
} from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import { CUSTOM_TOP_LEVEL_CATEGORY, OTHER_TOP_LEVEL_CATEGORY } from "./furniture.constants"
import { furnitureQueueKey } from "./furnitureQueueKey"
import { resolveCountertopSeriesAfterErefChange, resolveDeliveryDateAfterErefChange } from "./furnitureSidebar.helpers"
import { useFurnitureForm } from "./useFurnitureForm.hook"
import { useServerResync } from "components/ui/customWebviewer/shared/useServerResync.hook"
import type {
	TEcall,
	TEref,
	TFurniture,
	TFurnitureCustomization,
	TFurnitureFixedPriceConfig,
	TFurnitureStatus,
	TFurnitureEstimationConfig,
	TFurnitureCountertopAttachments,
	TPageElevationData,
	TSelectedModelNumber,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureCountertopAttachmentsRequest,
	TUpdateFurnitureCountertopAttachmentsResponse,
	TUpdateFurnitureCountertopSeriesResponse,
	TUpdateFurnitureCustomizationResponse,
	TUpdateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TCustomModelNumber } from "types/customModelNumbers.type"
import type { TModelNumberDetails } from "types/modelNumbers.type"
import type { TQuotationComponentOptionsResponse, TQuotationDetail } from "types/quotations.type"
import type { AxiosResponse } from "axios"
import style from "./FurnitureSidebar.module.scss"

const { sidebar, body, divider, content, segmentedWrapper, segmented } = style

type TabOption = "Details" | "Connected callouts"
const TAB_OPTIONS: TabOption[] = ["Details", "Connected callouts"]

const EMPTY_RELATED_OBJECTS: TPageElevationData[] = []

const bumpErefVersionInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
	newVersion: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const idx = page.erefs.findIndex((e) => e.id === erefId)
		if (idx === -1) return page
		changed = true
		const nextErefs = page.erefs.slice()
		nextErefs[idx] = { ...nextErefs[idx], version: newVersion }
		return { ...page, erefs: nextErefs }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

type Props = {
	furniture: TFurniture
	relatedObjects?: TPageElevationData[]
	connectedEcalls?: TEcall[]
	initialTab?: TabOption
	configuration?: TQuotationDetail["configuration"]
	onView?: (ecall: TEcall) => void
	onClose?: () => void
	onNavigateToEref?: (furniture: TFurniture) => void
	onDelete?: (furniture: TFurniture) => void
}

const isSameCustomization = (a: TFurnitureCustomization | null, b: TFurnitureCustomization | null) =>
	a === null || b === null ? a === b : a.note === b.note && a.price_adjustment === b.price_adjustment

const EMPTY_DELIVERY_DATES: NonNullable<TQuotationDetail["configuration"]>["delivery_dates"] = []
const EMPTY_COUNTERTOP_SERIES_OPTIONS: NonNullable<TQuotationComponentOptionsResponse["countertop_series_and_colors"]> = []

export const FurnitureSidebar = ({
	furniture,
	relatedObjects = EMPTY_RELATED_OBJECTS,
	connectedEcalls = [],
	initialTab = "Details",
	configuration,
	onView,
	onClose,
	onNavigateToEref,
	onDelete,
}: Props) => {
	const form = useFurnitureForm(furniture)
	const [tab, setTab] = useState<TabOption>(initialTab)

	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateFurnitureCategoryAsync } = useUpdateFurnitureCategory()
	const { mutateAsync: updateFurnitureFixedPriceAsync } = useUpdateFurnitureFixedPrice()
	const { mutateAsync: updateFurnitureAsync } = useUpdateFurniture()
	const { mutateAsync: updateFurnitureCountertopSeriesAsync } = useUpdateFurnitureCountertopSeries()
	const { mutateAsync: selectArbitraryModelNumberAsync } = useSelectArbitraryModelNumber()
	const { mutateAsync: linkFurnitureCustomModelNumberAsync } = useLinkFurnitureCustomModelNumber()
	const { mutateAsync: updateFurnitureConfigAsync } = useUpdateFurnitureConfig()
	const { mutateAsync: updateFurnitureCountertopAttachmentsAsync } = useUpdateFurnitureCountertopAttachments()
	const { mutateAsync: updateFurnitureCommentAsync } = useUpdateFurnitureComment()
	const { mutateAsync: deleteFurnitureCommentAsync } = useDeleteFurnitureComment()
	const { mutateAsync: updateFurnitureCustomizationAsync } = useUpdateFurnitureCustomization()
	const { mutateAsync: deleteFurnitureCustomizationAsync } = useDeleteFurnitureCustomization()
	const { mutateAsync: updateFurnitureStatusAsync, isPending: isStatusPending } = useUpdateFurnitureStatus()
	const { mutateAsync: connectFurnitureToEref } = useConnectFurnitureToEref()
	const { mutateAsync: disconnectFurnitureFromEref } = useDisconnectFurnitureFromEref()
	const { enqueue, versionRef, isPending: isSaving } = useEntityMutationQueue(
		furnitureQueueKey(furniture.id),
		furniture.version,
	)
	const { data: objectsCategoriesData } = useObjectsCategories()
	const savedWidthRef = useRef(furniture.width)
	const savedHeightRef = useRef(furniture.height)
	const savedDepthRef = useRef(furniture.depth)
	// Mirrors the price/name resyncs below: without it a dimension changed elsewhere (e.g. the
	// pop-out's peer window) left this ref stale, so `commitDimension`'s `value === savedRef.current`
	// guard could compare a locally-typed value against a value the server had already moved past.
	useEffect(() => {
		savedWidthRef.current = furniture.width
	}, [furniture.width])
	useEffect(() => {
		savedHeightRef.current = furniture.height
	}, [furniture.height])
	useEffect(() => {
		savedDepthRef.current = furniture.depth
	}, [furniture.depth])
	const savedAdjustedShelvesRef = useRef<number | null>(furniture.estimation_config?.adjusted_shelves ?? null)
	useEffect(() => {
		savedAdjustedShelvesRef.current = furniture.estimation_config?.adjusted_shelves ?? null
	}, [furniture.estimation_config?.adjusted_shelves])
	const savedGrommetsRef = useRef<number>(furniture.countertop_attachments?.grommets ?? 0)
	useEffect(() => {
		savedGrommetsRef.current = furniture.countertop_attachments?.grommets ?? 0
	}, [furniture.countertop_attachments?.grommets])
	const savedWallCleatsRef = useRef<number>(furniture.countertop_attachments?.wall_cleats ?? 0)
	useEffect(() => {
		savedWallCleatsRef.current = furniture.countertop_attachments?.wall_cleats ?? 0
	}, [furniture.countertop_attachments?.wall_cleats])
	const savedNotesRef = useRef(furniture.estimation_config_comment ?? "")
	// What the SERVER holds (REQ-413/414). `null` means "there is no customization to delete",
	// which is what tells the `−` button apart: on a saved one it fires the DELETE, on a section
	// that was only ever expanded it just folds away.
	const savedCustomizationRef = useRef<TFurnitureCustomization | null>(furniture.customization ?? null)
	const {
		expand: expandCustomization,
		collapse: collapseCustomization,
		payload: customizationPayload,
		resync: resyncCustomization,
	} = form.customization

	// Peer-window edits (#1430) — see useServerResync for the echo / queued-save guards.
	const { setNotes } = form
	useServerResync(furniture.estimation_config_comment ?? "", savedNotesRef, setNotes, { isSaving })

	const hasIncomingCustomization = furniture.customization != null
	const incomingCustomizationNote = furniture.customization?.note ?? ""
	const incomingCustomizationPrice = furniture.customization?.price_adjustment ?? null
	const incomingCustomization = useMemo<TFurnitureCustomization | null>(
		() =>
			hasIncomingCustomization
				? { note: incomingCustomizationNote, price_adjustment: incomingCustomizationPrice }
				: null,
		[hasIncomingCustomization, incomingCustomizationNote, incomingCustomizationPrice],
	)
	useServerResync(incomingCustomization, savedCustomizationRef, resyncCustomization, {
		isSaving,
		isEqual: isSameCustomization,
	})
	const savedPriceRef = useRef<number | null>(furniture.fixed_price_config?.fixed_price ?? null)
	useEffect(() => {
		savedPriceRef.current = furniture.fixed_price_config?.fixed_price ?? null
	}, [furniture.fixed_price_config?.fixed_price])
	const savedNameRef = useRef<string>(furniture.fixed_price_config?.name ?? "")
	useEffect(() => {
		savedNameRef.current = furniture.fixed_price_config?.name ?? ""
	}, [furniture.fixed_price_config?.name])

	const connectedEref = useMemo<TEref | null>(() => {
		if (!furniture.eref_id) return null
		for (const page of relatedObjects) {
			const found = page.erefs.find((e) => e.id === furniture.eref_id)
			if (found) return found
		}
		return null
	}, [relatedObjects, furniture.eref_id])
	const seriesOverrideId = connectedEref?.estimation_config?.series_override_id ?? null

	// REQ-405: the same option set the elevation's own "Countertops series" selector already fetches
	// (ConfigurationTab), fetched again here with the SAME fallback (the connected elevation's own
	// series override, or the quotation's default) — `useGetComponentOptions` caches by this exact
	// key, so a sidebar and its connected eref's config tab open at once share one request.
	const countertopSeriesId = connectedEref?.estimation_config?.series_override_id ?? configuration?.series_id ?? undefined
	const { data: countertopComponentOptions } = useGetComponentOptions({
		seriesId: countertopSeriesId,
		components: ["countertop_series_and_colors"],
		quotationId: Number.isFinite(quotationId) ? quotationId : undefined,
	})
	const countertopSeriesOptions = countertopComponentOptions?.countertop_series_and_colors ?? EMPTY_COUNTERTOP_SERIES_OPTIONS

	const commitDimension = useCallback(
		(key: "width" | "height" | "depth", value: number | null) => {
			if (!Number.isFinite(quotationId) || !quotationId) return
			const savedRef = key === "width" ? savedWidthRef : key === "height" ? savedHeightRef : savedDepthRef
			if (value === savedRef.current) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, [key]: value },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const updated = data?.this_furniture
					if (!updated) return
					versionRef.current = updated.version
					savedWidthRef.current = updated.width
					savedHeightRef.current = updated.height
					savedDepthRef.current = updated.depth
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", quotationId],
						(cached) => {
							const next = spliceFurnitureIntoCache(cached, updated)
							return ordering ? reorderFurnitureInCache(next, ordering) : next
						},
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError],
	)

	// `deliveryDateId: null` drops the override and returns the object to its elevation's date
	// (REQ-410). Available for every object, including countertops and fixed-price furniture.
	const commitDeliveryDate = useCallback(
		(deliveryDateId: number | null) => {
			if (!Number.isFinite(quotationId) || !quotationId) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, delivery_date_id: deliveryDateId },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const updated = data?.this_furniture
					if (!updated) return
					versionRef.current = updated.version
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", quotationId],
						(cached) => {
							const next = spliceFurnitureIntoCache(cached, updated)
							return ordering ? reorderFurnitureInCache(next, ordering) : next
						},
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError],
	)

	/**
	 * Countertop series override for this object (REQ-405). Dedicated endpoint — the general
	 * furniture PATCH does not carry this field — so the response shape differs from
	 * `commitDeliveryDate`'s (`{id, version, countertop_series_and_colors}`, no `this_furniture` /
	 * `ordering`; a countertop series change never re-sorts the group). `spliceFurnitureIntoCache`
	 * merges a partial patch, so the response is applied as-is. `null` clears the override and
	 * returns the object to inheriting its elevation's series.
	 */
	const commitCountertopSeries = useCallback(
		(countertopSeriesId: number | null) => {
			if (!Number.isFinite(quotationId) || !quotationId) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureCountertopSeriesAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, countertop_series_and_colors_id: countertopSeriesId },
					})
					const data = response?.data as TUpdateFurnitureCountertopSeriesResponse | undefined
					if (!data) return
					versionRef.current = data.version
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", quotationId],
						(cached) =>
							spliceFurnitureIntoCache(cached, {
								id: furniture.id,
								version: data.version,
								countertop_series_and_colors: data.countertop_series_and_colors,
							}),
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureCountertopSeriesAsync, furniture.id, queryClient, formatError],
	)

	/**
	 * Applies a model-number pick (or a clear, when `next` is `null`).
	 *
	 * Two endpoints, one flow: a pick from the modal's catalogue-wide scope goes
	 * through `select-arbitrary-model-number/`, which accepts a model number from ANY
	 * category and moves the furniture's `related_category` to match in the same
	 * transaction — the atomic save the modal's footer notice promises. Everything else
	 * (an object-category pick, or clearing the selection) keeps using the plain
	 * furniture PATCH, which rejects a cross-category id.
	 *
	 * Both responses carry `this_furniture` + `ordering`, so the cache handling below
	 * is shared: a category change re-sorts the furniture's group in the left and right
	 * sidebars, and `ordering` is what re-renders them without a refetch.
	 */
	const commitSelectedModelNumber = useCallback(
		(
			next: TSelectedModelNumber | null,
			options?: { useArbitraryEndpoint: boolean; customModelNumber?: TCustomModelNumber | null },
		): Promise<void> => {
			if (!Number.isFinite(quotationId) || !quotationId) return Promise.resolve()

			const customModelNumber = options?.customModelNumber ?? null

			return enqueue(async () => {
				try {
					const useArbitrary = options?.useArbitraryEndpoint === true && next !== null
					/**
					 * A custom pick (#1346) is ONE request, whatever the object was before: the
					 * custom-model-number endpoint converts a cabinet to a custom object and links the
					 * definition in the same transaction. Sending a `related_category_id: "custom"` PATCH
					 * first would burn a version for a state nothing ever renders.
					 */
					const response = customModelNumber
						? await linkFurnitureCustomModelNumberAsync({
								quotationId,
								furnitureId: furniture.id,
								body: { version: versionRef.current, custom_model_number_id: customModelNumber.id },
							})
						: useArbitrary
							? await selectArbitraryModelNumberAsync({
									quotationId,
									furnitureId: furniture.id,
									body: { version: versionRef.current, selected_model_number_id: next.id },
								})
							: await updateFurnitureAsync({
									quotationId,
									furnitureId: furniture.id,
									body: { version: versionRef.current, selected_model_number_id: next?.id ?? null },
								})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const updated = data?.this_furniture
					if (!updated) {
						// Optimistic fallback for a response that carries no furniture. The custom branch
						// mirrors the conversion locally so the panel doesn't keep showing the old cabinet.
						if (customModelNumber) form.setCustomModelNumber(customModelNumber)
						else form.setSelectedModelNumber(next)
						return
					}
					versionRef.current = updated.version
					/**
					 * `spliceFurnitureIntoCache` MERGES, so a conversion has to force-clear what the object
					 * used to be — the three category levels and the Stevens model number — or the stale
					 * cabinet identity survives in the cache and the object keeps rendering as one. Same
					 * reason `asOtherFurniture` exists for the Other conversion.
					 */
					const appliedFurniture = customModelNumber ? asCustomFurniture(updated) : updated
					// The highlight's label and fill are derived from the object's identity, which a
					// conversion changes; the canvas has to be repainted from the response, not left to a
					// refetch that may never come.
					if (customModelNumber) AnnotationService.updateFurnitureAnnotation(appliedFurniture)
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", quotationId],
						(cached) => {
							const nextCache = spliceFurnitureIntoCache(cached, appliedFurniture)
							return ordering ? reorderFurnitureInCache(nextCache, ordering) : nextCache
						},
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
					throw e
				}
			})
		},
		[
			form,
			quotationId,
			enqueue,
			versionRef,
			updateFurnitureAsync,
			selectArbitraryModelNumberAsync,
			linkFurnitureCustomModelNumberAsync,
			furniture.id,
			queryClient,
			formatError,
		],
	)

	const commitConfig = useCallback(
		(body: Omit<TUpdateFurnitureConfigRequest, "version">) => {
			if (!Number.isFinite(quotationId) || !quotationId) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureConfigAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, ...body },
					})
					const { version: responseVersion, ...configFields } =
						(response?.data as (Partial<TFurnitureEstimationConfig> & { version?: number }) | undefined) ?? {}
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					const baseConfig: TFurnitureEstimationConfig = furniture.estimation_config ?? {
						furniture_id: furniture.id,
						number_of_fillers: 0,
						number_of_finishing_ends: 0,
						lock_count: null,
						has_locks: false,
						has_finished_backs: false,
						adjusted_shelves: null,
					}

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, {
							...furniture,
							version: nextVersion,
							estimation_config: { ...baseConfig, ...configFields },
						}),
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureConfigAsync, furniture, queryClient, formatError],
	)

	/**
	 * REQ-449. Mirrors `commitConfig`'s merge-into-cache shape, keyed on `countertop_attachments`
	 * instead of `estimation_config`. The response nests the counts under `countertop_attachments`;
	 * that object is the truth after the write, so it replaces the cached one outright. Only when
	 * it is missing does the local base + the sent fields stand in for it.
	 */
	const commitCountertopAttachments = useCallback(
		(body: Omit<TUpdateFurnitureCountertopAttachmentsRequest, "version">) => {
			if (!Number.isFinite(quotationId) || !quotationId) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureCountertopAttachmentsAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, ...body },
					})
					const data = response?.data as Partial<TUpdateFurnitureCountertopAttachmentsResponse> | undefined
					const responseVersion = data?.version
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					const baseAttachments: TFurnitureCountertopAttachments = furniture.countertop_attachments ?? {
						grommets: 0,
						wall_cleats: 0,
					}

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, {
							...furniture,
							version: nextVersion,
							countertop_attachments: data?.countertop_attachments ?? { ...baseAttachments, ...body },
						}),
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureCountertopAttachmentsAsync, furniture, queryClient, formatError],
	)

	const commitNotes = useCallback(() => {
		const next = form.notes
		if (next === savedNotesRef.current) return
		if (!Number.isFinite(quotationId) || !quotationId) return
		savedNotesRef.current = next

		const applyCommentInCache = (commentValue: string | null, nextVersion: number) => {
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
				spliceFurnitureIntoCache(cached, {
					...furniture,
					version: nextVersion,
					estimation_config_comment: commentValue,
				}),
			)
		}

		enqueue(async () => {
			try {
				const response =
					next.trim() === ""
						? await deleteFurnitureCommentAsync({
								quotationId,
								furnitureId: furniture.id,
								version: versionRef.current,
							})
						: await updateFurnitureCommentAsync({
								quotationId,
								furnitureId: furniture.id,
								body: { version: versionRef.current, comment: next },
							})
				const responseVersion = (response?.data as { version?: number } | undefined)?.version
				const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
				versionRef.current = nextVersion
				applyCommentInCache(next.trim() === "" ? null : next, nextVersion)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		form.notes,
		quotationId,
		enqueue,
		versionRef,
		updateFurnitureCommentAsync,
		deleteFurnitureCommentAsync,
		furniture,
		queryClient,
		formatError,
	])

	/**
	 * REQ-413/414 — creates the object's customization or overwrites the existing one. There is
	 * only ever one per object, so this is the single write path: opening the section, typing a
	 * note and typing an amount all land here, on the same PUT.
	 *
	 * Takes the values explicitly rather than reading state, because `+` has to write the empty
	 * customization in the same tick it expands the section — the state it set is not readable
	 * yet at that point. Neither field is required: `{ note: "", price_adjustment: null }` is a
	 * customization the endpoint stores happily, and clearing a field is an update, never a
	 * delete. Removal is the header's `−` alone (see `removeCustomization`).
	 */
	const commitCustomizationValues = useCallback(
		(next: TFurnitureCustomization) => {
			const saved = savedCustomizationRef.current
			if (saved && saved.note === next.note && saved.price_adjustment === next.price_adjustment) return
			if (!Number.isFinite(quotationId) || !quotationId) return
			// Claimed optimistically so a second blur with unchanged values is a no-op, and restored
			// in the catch: leaving it claiming a rejected write is persisted would both block the
			// retry (the equality check above) and make `−` DELETE a customization the server never
			// stored, which answers 404 and toasts a second time.
			const previouslySaved = savedCustomizationRef.current
			savedCustomizationRef.current = next

			enqueue(async () => {
				try {
					const response = await updateFurnitureCustomizationAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, note: next.note, price_adjustment: next.price_adjustment },
					})
					const data = response?.data as TUpdateFurnitureCustomizationResponse | undefined
					const nextVersion = typeof data?.version === "number" ? data.version : versionRef.current + 1
					versionRef.current = nextVersion
					const applied: TFurnitureCustomization = {
						note: data?.note ?? next.note,
						price_adjustment: data?.price_adjustment !== undefined ? data.price_adjustment : next.price_adjustment,
					}
					savedCustomizationRef.current = applied
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, { ...furniture, version: nextVersion, customization: applied }),
					)
				} catch (e) {
					savedCustomizationRef.current = previouslySaved
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureCustomizationAsync, furniture, queryClient, formatError],
	)

	/**
	 * The header's `+`. Opening the section IS creating the customization: the object gets one
	 * straight away, empty, and the two fields are filled in afterwards — so a user who opens the
	 * section and walks away still has a customization waiting, rather than a form that quietly
	 * saved nothing.
	 */
	const addCustomization = useCallback(() => {
		expandCustomization()
		commitCustomizationValues({ note: "", price_adjustment: null })
	}, [expandCustomization, commitCustomizationValues])

	/** The fields' blur commit — whatever they hold, including one of them empty. */
	const commitCustomization = useCallback(() => {
		if (customizationPayload === null) return
		commitCustomizationValues(customizationPayload)
	}, [customizationPayload, commitCustomizationValues])

	/**
	 * The header's `−` (REQ-414's "remove"). The section always folds away and clears; whether
	 * anything is SENT depends on there being something on the server to delete, which is what
	 * `savedCustomizationRef` tracks — so a `+` whose create was rejected collapses with no
	 * request at all.
	 */
	const removeCustomization = useCallback(() => {
		const previouslySaved = savedCustomizationRef.current
		collapseCustomization()
		if (previouslySaved === null) return
		if (!Number.isFinite(quotationId) || !quotationId) return
		// Restored in the catch for the mirror-image reason `commitCustomization` restores it: a
		// failed DELETE leaves the customization on the server, and a ref claiming otherwise would
		// make a second `−` silently do nothing.
		savedCustomizationRef.current = null

		enqueue(async () => {
			try {
				const response = await deleteFurnitureCustomizationAsync({
					quotationId,
					furnitureId: furniture.id,
					version: versionRef.current,
				})
				const responseVersion = (response?.data as { version?: number } | undefined)?.version
				const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
				versionRef.current = nextVersion
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
					spliceFurnitureIntoCache(cached, { ...furniture, version: nextVersion, customization: null }),
				)
			} catch (e) {
				savedCustomizationRef.current = previouslySaved
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		collapseCustomization,
		quotationId,
		enqueue,
		versionRef,
		deleteFurnitureCustomizationAsync,
		furniture,
		queryClient,
		formatError,
	])

	const handleErefChange = useCallback(
		async (nextErefId: number | null) => {
			form.setSelectedErefId(nextErefId)

			const previousErefId = furniture.eref_id
			if (nextErefId === previousErefId) return
			if (!Number.isFinite(quotationId) || !quotationId) return

			// Versions for both the current and the new Eref are required for optimistic locking —
			// pull them from the cached relatedObjects.
			let previousEref: TEref | undefined
			let nextEref: TEref | undefined
			for (const page of relatedObjects) {
				if (!previousEref && previousErefId !== null) {
					previousEref = page.erefs.find((e) => e.id === previousErefId)
				}
				if (!nextEref && nextErefId !== null) {
					nextEref = page.erefs.find((e) => e.id === nextErefId)
				}
				if ((previousErefId === null || previousEref) && (nextErefId === null || nextEref)) break
			}
			if (nextErefId !== null && !nextEref) return

			await enqueue(async () => {
				try {
					let markPreviousUnconnected = false

					// 1) Disconnect from the current Eref (if any).
					if (previousErefId !== null && previousEref) {
						const disconnectResponse = await disconnectFurnitureFromEref({
							quotationId,
							erefId: previousErefId,
							body: {
								eref_version: previousEref.version,
								furniture: [{ id: furniture.id, version: versionRef.current }],
							},
						})
						const disconnectVersion = (disconnectResponse?.data as { version?: number } | undefined)?.version
						versionRef.current = typeof disconnectVersion === "number" ? disconnectVersion : versionRef.current + 1

						const nextPreviousErefVersion = previousEref.version + 1
						queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
							["quotation", quotationId],
							(cached) => bumpErefVersionInCache(cached, previousErefId, nextPreviousErefVersion),
						)

						markPreviousUnconnected = !relatedObjects.some((page) =>
							page.furniture.some((f) => f.id !== furniture.id && f.eref_id === previousErefId),
						)
					}

					// 2) Connect to the new Eref (if any).
					if (nextErefId !== null && nextEref) {
						const connectResponse = await connectFurnitureToEref({
							quotationId,
							erefId: nextErefId,
							body: {
								eref_version: nextEref.version,
								furniture: [{ id: furniture.id, version: versionRef.current }],
							},
						})
						const connectVersion = (connectResponse?.data as { version?: number } | undefined)?.version
						versionRef.current = typeof connectVersion === "number" ? connectVersion : versionRef.current + 1

						const nextNextErefVersion = nextEref.version + 1
						queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
							["quotation", quotationId],
							(cached) => bumpErefVersionInCache(cached, nextErefId, nextNextErefVersion),
						)
					}

					const nextDeliveryDate = resolveDeliveryDateAfterErefChange(furniture.delivery_date ?? null, nextEref)
					const nextCountertopSeries = resolveCountertopSeriesAfterErefChange(
						furniture.countertop_series_and_colors ?? null,
						nextEref,
					)

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, {
							...furniture,
							version: versionRef.current,
							eref_id: nextErefId,
							delivery_date: nextDeliveryDate,
							countertop_series_and_colors: nextCountertopSeries,
						}),
					)

					// Apply both eref status changes together (no await between them) so an ecall
					// linked to BOTH erefs settles on its final color in one synchronous pass,
					// instead of flashing through an intermediate color while the connect request
					// is still in flight.
					if (markPreviousUnconnected && previousErefId !== null) {
						AnnotationService.updateErefAnnotationStatus(previousErefId, "unconnected")
					}
					if (nextErefId !== null && nextEref) {
						AnnotationService.updateErefAnnotationStatus(nextErefId, "connected")
					}
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form,
			furniture,
			relatedObjects,
			quotationId,
			enqueue,
			versionRef,
			connectFurnitureToEref,
			disconnectFurnitureFromEref,
			queryClient,
			formatError,
		],
	)

	/**
	 * Converting to a custom object (#1346) from the top-level dropdown.
	 *
	 * Deliberately NO confirmation modal — the issue asks for parity with the other type changes,
	 * which all commit straight from the select. `related_category_id` carries the literal string
	 * `"custom"`; the backend clears the category, disconnects the Stevens model number, drops the
	 * pricing config and creates an empty `custom_object_config`.
	 */
	const commitCustomConversion = useCallback(() => {
		if (!Number.isFinite(quotationId) || !quotationId) return
		if (isCustomFurniture(furniture)) return

		enqueue(async () => {
			try {
				const response = await updateFurnitureCategoryAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: versionRef.current, related_category_id: "custom" },
				})
				const data = response?.data as TUpdateFurnitureResponse | undefined
				const responseVersion = data?.this_furniture?.version
				const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
				versionRef.current = nextVersion

				// The category PATCH answers with a PARTIAL furniture (id, version, related_category_id,
				// model_numbers) — no `custom_object_config` — so the conversion has to be applied
				// locally. `asCustomFurniture` is what puts one in place and clears the old identity that
				// `spliceFurnitureIntoCache` would otherwise merge straight back in.
				const updatedFurniture: TFurniture = asCustomFurniture({
					...furniture,
					...(data?.this_furniture ?? {}),
					version: nextVersion,
				})
				AnnotationService.updateFurnitureAnnotation(updatedFurniture)
				const ordering = data?.ordering
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const next = spliceFurnitureIntoCache(cached, updatedFurniture)
					return ordering ? reorderFurnitureInCache(next, ordering) : next
				})
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		furniture,
		quotationId,
		enqueue,
		versionRef,
		updateFurnitureCategoryAsync,
		queryClient,
		formatError,
	])

	const handleTopLevelCategoryChange = useCallback(
		(value: string | number | null) => {
			form.categorySelection.handleTopLevelChange(value)

			if (value === CUSTOM_TOP_LEVEL_CATEGORY) {
				commitCustomConversion()
				return
			}

			if (value !== OTHER_TOP_LEVEL_CATEGORY) return
			if (!Number.isFinite(quotationId) || !quotationId) return
			if (isOtherFurniture(furniture)) return

			// Backend clears the furniture's attachments config when the category becomes
			// "Other"; mirror that on the client so the form reflects the new defaults.
			// Locks and Finished Backs inherit from the parent eref (false if unattached).
			const nextHasLocks = connectedEref?.estimation_config?.has_locks ?? false
			const nextHasFinishedBacks = connectedEref?.estimation_config?.has_finished_backs ?? false
			form.setAdjustableShelves(0)
			form.setHasLocks(nextHasLocks ? "yes" : "no")
			form.setHasFinishedBacks(nextHasFinishedBacks ? "yes" : "no")
			form.setFillings(0)
			form.setFinishingEnds(0)

			enqueue(async () => {
				try {
					const response = await updateFurnitureCategoryAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, related_category_id: null },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const responseVersion = data?.this_furniture?.version
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					// Prefer the authoritative furniture from the response; the hand-built
					// fallback only mirrors the backend's "Other" reset when this_furniture
					// is somehow absent (pre-#1767 behaviour). asOtherFurniture force-clears
					// every category level so the "Other" label shows even if this_furniture
					// omits those fields (spliceFurnitureIntoCache merges, so a stale category
					// would otherwise survive).
					const updatedFurniture: TFurniture = asOtherFurniture(
						data?.this_furniture ?? {
							...furniture,
							version: nextVersion,
							estimation_config: {
								furniture_id: furniture.id,
								lock_count: furniture.estimation_config?.lock_count ?? null,
								has_locks: nextHasLocks,
								has_finished_backs: nextHasFinishedBacks,
								number_of_fillers: 0,
								number_of_finishing_ends: 0,
								adjusted_shelves: 0,
							},
						},
					)
					AnnotationService.updateFurnitureAnnotation(updatedFurniture)
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", quotationId],
						(cached) => {
							const next = spliceFurnitureIntoCache(cached, updatedFurniture)
							return ordering ? reorderFurnitureInCache(next, ordering) : next
						},
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form,
			furniture,
			quotationId,
			enqueue,
			versionRef,
			queryClient,
			formatError,
			updateFurnitureCategoryAsync,
			connectedEref,
			commitCustomConversion,
		],
	)

	const commitPrice = useCallback(() => {
		const next = form.price
		if (next === savedPriceRef.current) return
		if (next === null) return
		if (!Number.isFinite(quotationId) || !quotationId) return

		savedPriceRef.current = next
		enqueue(async () => {
			try {
				const response = await updateFurnitureFixedPriceAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: versionRef.current, fixed_price: next },
				})
				const data = response?.data as
					| { version?: number; fixed_price_config?: TFurnitureFixedPriceConfig | null }
					| undefined
				const nextVersion = typeof data?.version === "number" ? data.version : versionRef.current + 1
				const nextPrice =
					typeof data?.fixed_price_config?.fixed_price === "number" ? data.fixed_price_config.fixed_price : next
				// Read the latest known name from savedNameRef (updated synchronously by
				// commitName before its mutation runs) so a queued price commit can't
				// overwrite a name that the user just typed and blurred.
				const nextName =
					data?.fixed_price_config?.name !== undefined
						? data.fixed_price_config?.name ?? null
						: savedNameRef.current === ""
							? null
							: savedNameRef.current
				versionRef.current = nextVersion
				savedPriceRef.current = nextPrice

				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
					spliceFurnitureIntoCache(cached, {
						...furniture,
						version: nextVersion,
						fixed_price_config: { fixed_price: nextPrice, name: nextName },
					}),
				)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		form.price,
		quotationId,
		enqueue,
		versionRef,
		furniture,
		queryClient,
		formatError,
		updateFurnitureFixedPriceAsync,
	])

	const commitName = useCallback(() => {
		const trimmed = form.name.trim()
		if (trimmed === savedNameRef.current) return
		if (!Number.isFinite(quotationId) || !quotationId) return

		savedNameRef.current = trimmed
		const bodyName: string | null = trimmed === "" ? null : trimmed
		enqueue(async () => {
			try {
				const response = await updateFurnitureAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: versionRef.current, name: bodyName },
				})
				const data = response?.data as TUpdateFurnitureResponse | undefined
				const updated = data?.this_furniture
				if (!updated) return
				if (typeof updated.version === "number") versionRef.current = updated.version
				savedNameRef.current = updated.fixed_price_config?.name ?? ""
				const ordering = data?.ordering
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const next = spliceFurnitureIntoCache(cached, updated)
					return ordering ? reorderFurnitureInCache(next, ordering) : next
				})
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [form.name, quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError])

	const handleObjectCategoryChange = useCallback(
		(value: string | number | null) => {
			form.categorySelection.handleModelCategoryChange(value)

			const nextId = typeof value === "number" ? value : value !== null ? Number(value) : null
			if (!Number.isFinite(nextId) || nextId === null || nextId === furniture.category?.id) return
			if (!Number.isFinite(quotationId) || !quotationId) return

			const nextCategoryName = form.categorySelection.modelCategoryOptions.find((opt) => opt.value === nextId)
				?.label as string | undefined
			const nextSuperCategoryName =
				typeof form.categorySelection.selectedSuperCategory === "string"
					? form.categorySelection.selectedSuperCategory
					: furniture.super_category?.name
			const nextTopLevelCategoryName =
				typeof form.categorySelection.selectedTopLevelCategory === "string"
					? form.categorySelection.selectedTopLevelCategory
					: furniture.top_level_category?.name

			// Pull model_label from the cached categories — the typed shape doesn't declare
			// `label`, but the API returns it on each category at runtime.
			type CategoryLite = { id: number; name?: string; label?: string; model_label?: string }
			type CategoriesByTopLevel = Record<string, Record<string, CategoryLite[]>>
			const supercategoryBucket = (objectsCategoriesData as CategoriesByTopLevel | undefined)?.[
				String(nextTopLevelCategoryName ?? "")
			]?.[String(nextSuperCategoryName ?? "")]
			const matchedCategory = supercategoryBucket?.find((c) => c.id === nextId)
			const nextModelLabel = matchedCategory?.label ?? matchedCategory?.model_label ?? ""

			enqueue(async () => {
				try {
					const response = await updateFurnitureCategoryAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, related_category_id: nextId },
					})
					// The category change cascades (model number disconnect, etc.), so the cache
					// is refetched below rather than spliced; the refetch returns the furniture
					// already in the backend-decided order. Read the new version from the
					// response when present, otherwise fall back to the +1 optimistic-locking bump.
					const responseVersion = (response?.data as TUpdateFurnitureResponse | undefined)?.this_furniture?.version
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					const updatedFurniture: TFurniture = {
						...furniture,
						version: nextVersion,
						category: {
							id: nextId,
							name: nextCategoryName ?? furniture.category?.name ?? "",
							model_label: nextModelLabel,
						},
						super_category: {
							id: furniture.super_category?.id ?? 0,
							name: nextSuperCategoryName ?? furniture.super_category?.name ?? "",
						},
						top_level_category: {
							id: furniture.top_level_category?.id ?? 0,
							name: nextTopLevelCategoryName ?? furniture.top_level_category?.name ?? "",
						},
						// Backend disconnects the model number when category changes
						selected_model_number: null,
						// ...and drops the custom-object config when moving to a real category (#1346).
						// Cleared locally too, or the annotation repainted just below still reads the
						// object as a custom one.
						custom_object_config: null,
					}
					AnnotationService.updateFurnitureAnnotation(updatedFurniture)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form.categorySelection,
			furniture,
			quotationId,
			enqueue,
			versionRef,
			queryClient,
			formatError,
			updateFurnitureCategoryAsync,
			objectsCategoriesData,
		],
	)

	const pageErefs = useMemo<TEref[]>(() => {
		const page = relatedObjects.find((p) => p.page_number === furniture.page_number)
		return page?.erefs ?? []
	}, [relatedObjects, furniture.page_number])

	const title = (form.selectedModelNumber?.model_number && furniture.category?.name) || "Object"
	const { data: catalogueImage } = useGetModelNumberImageLink(form.selectedModelNumber?.id ?? 0)
	const imageUrl = catalogueImage?.download_url ?? null

	const { data: selectedModelNumberDetails } = useGetModelNumber(form.selectedModelNumber?.id ?? 0)
	const { data: seriesList } = useGetSeries()
	const missingPriceSeriesName = useMemo<string | null>(() => {
		if (seriesOverrideId === null) return null
		const details = selectedModelNumberDetails as TModelNumberDetails | undefined
		if (!details) return null
		const hasPrice = details.pricing_info?.some((p) => p.series.id === seriesOverrideId) ?? false
		if (hasPrice) return null
		return seriesList?.find((s) => s.id === seriesOverrideId)?.series_name ?? null
	}, [seriesOverrideId, selectedModelNumberDetails, seriesList])

	const showAttachments = form.variant === "typical"
	// REQ-449: "shown only when the selected furniture is a countertop."
	const showCountertopAttachments = form.variant === "countertop"
	// A custom object HAS a model number — its own definition — so the section stays, showing the
	// linked definition as a chip exactly as a cabinet shows its Stevens one.
	const showModelNumber = form.variant === "typical" || form.variant === "custom"
	const showPrice = form.variant === "other"
	const showName = form.variant === "other"

	const updateStatus = useCallback(
		(next: TFurnitureStatus) => {
			if (!Number.isFinite(quotationId) || !quotationId) return
			enqueue(async () => {
				try {
					const response = await updateFurnitureStatusAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, status: next },
					})
					const updated = response?.data as TFurniture | undefined
					if (!updated) return
					versionRef.current = updated.version
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, updated),
					)
					AnnotationService.updateFurnitureEntityStatus(furniture.id, next)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureStatusAsync, furniture.id, queryClient, formatError],
	)

	return (
		<div className={sidebar} data-hotkey-sidebar tabIndex={-1}>
			<FurnitureHeader
				title={title}
				status={furniture.status}
				onClose={onClose}
				onBack={furniture.eref_id != null && onNavigateToEref ? () => onNavigateToEref(furniture) : undefined}
				onDelete={onDelete ? () => onDelete(furniture) : undefined}
			/>
			<div className={content}>
				<div className={segmentedWrapper}>
					<SegmentedWithoutBg
						options={TAB_OPTIONS}
						value={tab}
						onChange={(value) => setTab(value as TabOption)}
						size="middle"
						className={segmented}
					/>
				</div>
				{tab === "Connected callouts" ? (
					<ConnectedCalloutsTab ecalls={connectedEcalls} erefId={furniture.eref_id} onView={(e) => onView?.(e)} />
				) : (
					<div className={body}>
						<FurnitureImage
							imageUrl={imageUrl}
							isCountertop={form.isCountertopItem}
							hasModelNumber={form.hasModelNumber}
							isOther={form.variant === "other"}
							isCustom={form.variant === "custom"}
						/>

						<ElevationReferenceSelect erefs={pageErefs} value={form.selectedErefId} onChange={handleErefChange} />

						<DeliveryDateSection
							deliveryDates={configuration?.delivery_dates ?? EMPTY_DELIVERY_DATES}
							value={furniture.delivery_date ?? null}
							hasEref={furniture.eref_id != null}
							onChange={commitDeliveryDate}
							onReset={() => commitDeliveryDate(null)}
						/>

						{form.variant === "countertop" ? (
							<CountertopSeriesSection
								options={countertopSeriesOptions}
								value={furniture.countertop_series_and_colors ?? null}
								hasEref={furniture.eref_id != null}
								onChange={commitCountertopSeries}
								onReset={() => commitCountertopSeries(null)}
							/>
						) : null}

						<div className={divider} />

						{showModelNumber ? (
							<>
								<ModelNumberSection
									furniture={furniture}
									selectedModelNumber={form.selectedModelNumber}
									seriesOverrideId={seriesOverrideId}
									missingPriceSeriesName={missingPriceSeriesName}
									onSelect={commitSelectedModelNumber}
									isOther={form.variant === "other"}
									isCustom={form.variant === "custom"}
									customModelNumber={form.customModelNumber}
									isRequired={form.variant !== "other"}
								/>
								<div className={divider} />
							</>
						) : null}

						<DimensionsSection
							width={form.width}
							height={form.height}
							depth={form.depth}
							onWidthChange={form.setWidth}
							onHeightChange={form.setHeight}
							onDepthChange={form.setDepth}
							onWidthCommit={() => commitDimension("width", form.width)}
							onHeightCommit={() => commitDimension("height", form.height)}
							onDepthCommit={() => commitDimension("depth", form.depth)}
							isRequired={form.variant !== "other"}
						/>

						{showName ? (
							<>
								<div className={divider} />
								<NameSection value={form.name} onChange={form.setName} onBlur={commitName} />
							</>
						) : null}

						{showPrice ? (
							<>
								{showName ? null : <div className={divider} />}
								<PriceSection value={form.price} onChange={form.setPrice} onBlur={commitPrice} />
							</>
						) : null}

						{showAttachments ? (
							<>
								<div className={divider} />
								<AttachmentsSection
									variant="cabinet"
									adjustableShelves={form.adjustableShelves}
									onAdjustableShelvesChange={form.setAdjustableShelves}
									onAdjustableShelvesCommit={() => {
										if (form.adjustableShelves === savedAdjustedShelvesRef.current) return
										savedAdjustedShelvesRef.current = form.adjustableShelves
										commitConfig({ adjusted_shelves: form.adjustableShelves })
									}}
									hasLocks={form.hasLocks}
									onHasLocksChange={(value) => {
										form.setHasLocks(value)
										commitConfig({ has_locks: value === "yes" })
									}}
									hasFinishedBacks={form.hasFinishedBacks}
									onHasFinishedBacksChange={(value) => {
										form.setHasFinishedBacks(value)
										commitConfig({ has_finished_backs: value === "yes" })
									}}
									fillings={form.fillings}
									onFillingsChange={(value) => {
										form.setFillings(value)
										commitConfig({ number_of_fillers: value })
									}}
									finishingEnds={form.finishingEnds}
									onFinishingEndsChange={(value) => {
										form.setFinishingEnds(value)
										commitConfig({ number_of_finishing_ends: value })
									}}
								/>
							</>
						) : null}

						{showCountertopAttachments ? (
							<>
								<div className={divider} />
								<AttachmentsSection
									variant="countertop"
									grommets={form.grommets}
									onGrommetsChange={form.setGrommets}
									onGrommetsCommit={() => {
										if (form.grommets === null) {
											form.setGrommets(savedGrommetsRef.current)
											return
										}
										if (form.grommets === savedGrommetsRef.current) return
										savedGrommetsRef.current = form.grommets
										commitCountertopAttachments({ grommets: form.grommets })
									}}
									wallCleats={form.wallCleats}
									onWallCleatsChange={form.setWallCleats}
									onWallCleatsCommit={() => {
										if (form.wallCleats === null) {
											form.setWallCleats(savedWallCleatsRef.current)
											return
										}
										if (form.wallCleats === savedWallCleatsRef.current) return
										savedWallCleatsRef.current = form.wallCleats
										commitCountertopAttachments({ wall_cleats: form.wallCleats })
									}}
								/>
							</>
						) : null}

						<div className={divider} />
						<CustomizationSection
							isExpanded={form.customization.isExpanded}
							note={form.customization.note}
							priceAdjustment={form.customization.priceAdjustment}
							onExpand={addCustomization}
							onRemove={removeCustomization}
							onNoteChange={form.customization.setNote}
							onPriceAdjustmentChange={form.customization.setPriceAdjustment}
							onCommit={commitCustomization}
						/>

						<div className={divider} />
						<NotesSection value={form.notes} onChange={form.setNotes} onBlur={commitNotes} />

						<div className={divider} />

						<CategoriesSection
							topLevelValue={form.categorySelection.selectedTopLevelCategory}
							topLevelOptions={form.categorySelection.topLevelOptions}
							onTopLevelChange={handleTopLevelCategoryChange}
							superValue={form.categorySelection.selectedSuperCategory}
							superOptions={form.categorySelection.superCategoryOptions}
							onSuperChange={form.categorySelection.handleSuperCategoryChange}
							modelCategoryValue={form.categorySelection.selectedModelCategory}
							modelCategoryOptions={form.categorySelection.modelCategoryOptions}
							onModelCategoryChange={handleObjectCategoryChange}
						/>
					</div>
				)}
			</div>
			{tab === "Details" ? (
				<FurnitureFooter
					isReviewed={form.isReviewed}
					isInQuotation={form.isInQuotation}
					isPending={isStatusPending}
					onToggleReviewed={() => updateStatus(form.isReviewed ? "work_in_progress" : "reviewed")}
					canAddToQuotation={form.isValid && missingPriceSeriesName === null}
					onAddToQuotation={() => updateStatus("in_quotation")}
					onRemoveFromQuotation={() => updateStatus("reviewed")}
				/>
			) : null}
		</div>
	)
}
